/**
 * PeopleStoryTabs — unified navigation across the "people story" pages:
 *
 *   • Our AI Team        → /our-team
 *   • Leadership Team    → /our-leaders
 *   • Founder's Message — JapanETCcard       → /founders-message/japanetccard
 *   • Founder's Message — JapanTollReceipts  → /founders-message/japantollreceipts
 *
 * Mounted at the top of all four pages so visitors can jump between
 * related context with one click. The active tab is inferred from the
 * current pathname.
 */
import { Link, useLocation } from "react-router-dom";
import { Bot, Users, BookOpen } from "lucide-react";

export default function PeopleStoryTabs({ lang = "en" }) {
  const { pathname } = useLocation();
  const isJa = lang === "ja";

  const tabs = [
    {
      key: "ai-team",
      path: `/${lang}/our-team`,
      en: "Our AI Team",
      ja: "AIチーム",
      icon: Bot,
      match: pathname.includes("/our-team"),
      // Blue — digital / AI surface
      activeBg: "#0E5FB0",
    },
    {
      key: "leadership",
      path: `/${lang}/our-leaders`,
      en: "Leadership Team",
      ja: "リーダーシップ",
      icon: Users,
      match: pathname.includes("/our-leaders"),
      // NoJo logo purple
      activeBg: "#663399",
    },
    {
      key: "founder-jec",
      path: `/${lang}/founders-message/japanetccard`,
      en: "Founder's Message — JapanETCcard",
      ja: "代表メッセージ・JapanETCcard",
      icon: BookOpen,
      match: pathname.includes("/founders-message/japanetccard"),
      // JapanETCcard brand purple
      activeBg: "#5B2E83",
    },
    {
      key: "founder-jtr",
      path: `/${lang}/founders-message/japantollreceipts`,
      en: "Founder's Message — JapanTollReceipts",
      ja: "代表メッセージ・JapanTollReceipts",
      icon: BookOpen,
      match: pathname.includes("/founders-message/japantollreceipts"),
      // JapanTollReceipts brand green
      activeBg: "#2F8F3C",
    },
  ];

  return (
    <nav
      aria-label={isJa ? "チームと代表メッセージの切り替え" : "People & founder story navigation"}
      data-testid="people-story-tabs"
      className="bg-white border-b border-[#E2E8F0] overflow-x-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-3">
        {/* Horizontal scroll on mobile keeps all four tabs on a single
            row instead of wrapping onto multiple lines and pushing the
            hero down. `overflow-x-auto` + `flex-nowrap` + thin-scrollbar
            gives a clean, drag-able strip. */}
        <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {tabs.map((t) => {
            const Icon = t.icon;
            const labelEn = t.shortEn || t.en;
            const labelJa = t.shortJa || t.ja;
            return (
              <Link
                key={t.key}
                to={t.path}
                data-testid={`people-story-tab-${t.key}`}
                aria-current={t.match ? "page" : undefined}
                style={t.match ? { backgroundColor: t.activeBg } : undefined}
                className={[
                  "shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold tracking-tight transition-all whitespace-nowrap",
                  t.match
                    ? "text-white shadow-sm"
                    : "text-[#475569] bg-slate-100 hover:bg-slate-200",
                ].join(" ")}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="sm:hidden">{isJa ? labelJa : labelEn}</span>
                <span className="hidden sm:inline">{isJa ? t.ja : t.en}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
